// Base API client implementation
import axios, {AxiosInstance, AxiosRequestConfig} from 'axios';
import {getIdToken, clearTokens} from '../utils/authStorage';
import {refreshIdToken, getCognitoAuthUrl} from '../config/authConfig';

export interface ApiRequestConfig extends AxiosRequestConfig {
  // Add custom configuration properties here
}

class ApiClient {
  private client: AxiosInstance;

  constructor(baseURL: string = import.meta.env.VITE_API_URL ?? '') {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - add auth token
    this.client.interceptors.request.use(
      config => {
        const token = getIdToken();
        if (token) {
          config.headers.Authorization = token;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      response => response.data,
      async error => {
        if (error.response?.status === 401 && !error.config._retry) {
          error.config._retry = true;
          const idToken = await refreshIdToken();
          if (idToken) {
            error.config.headers.Authorization = idToken;
            return this.client(error.config);
          }
          clearTokens();
          window.location.href = await getCognitoAuthUrl();
          return Promise.reject(error);
        }
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  public get<T = unknown>(url: string, config?: ApiRequestConfig): Promise<T> {
    return this.client.get(url, config);
  }

  public post<T = unknown>(
    url: string,
    data?: unknown,
    config?: ApiRequestConfig
  ): Promise<T> {
    return this.client.post(url, data, config);
  }

  public put<T = unknown>(
    url: string,
    data?: unknown,
    config?: ApiRequestConfig
  ): Promise<T> {
    return this.client.put(url, data, config);
  }

  public delete<T = unknown>(
    url: string,
    config?: ApiRequestConfig
  ): Promise<T> {
    return this.client.delete(url, config);
  }

  public patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: ApiRequestConfig
  ): Promise<T> {
    return this.client.patch(url, data, config);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
